import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.string().uuid();
const JsonObjectSchema = z.record(z.string(), z.unknown());

const RunStatusSchema = z.enum([
  "queued",
  "running",
  "completed",
  "failed",
  // Stopped by a user before it finished. The ai-service task may still be
  // draining in the background, but its ingest posts are rejected once cancelled.
  "cancelled",
]);

// One selected Strands evaluator + its knobs (mirrors the ai-service EvaluatorConfig).
// `threshold` (when set) turns the evaluator's 0–1 score into pass/fail; otherwise the
// evaluator's own verdict is used. `rubric` only applies to rubric-based evaluators.
const EvaluatorConfigSchema = z.object({
  key: z.string(),
  threshold: z.number().min(0).max(1).optional(),
  judgeModel: z.string().optional(),
  rubric: z.string().optional(),
});

// The Strands evaluator catalog the UI renders as toggles. Static — fixed by the pinned
// strands-agents-evals version; mirrors the ai-service registry
// (agent_eval/strands_evaluators.py EVALUATOR_SPECS). Phase-3 (agentic) evaluators need
// the trajectory trace and are shown greyed/disabled until then.
export const AGENT_EVAL_EVALUATOR_CATALOG = [
  {
    key: "helpfulness",
    label: "Helpfulness",
    group: "quality",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "faithfulness",
    label: "Faithfulness",
    group: "quality",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "correctness",
    label: "Correctness",
    group: "quality",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "coherence",
    label: "Coherence",
    group: "quality",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "conciseness",
    label: "Conciseness",
    group: "quality",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "response_relevance",
    label: "Response relevance",
    group: "quality",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "instruction_following",
    label: "Instruction following",
    group: "quality",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "output",
    label: "Custom rubric",
    group: "quality",
    phase: 1,
    needsRubric: true,
    isMultimodal: false,
  },
  {
    key: "harmfulness",
    label: "Harmfulness",
    group: "safety",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "refusal",
    label: "Refusal",
    group: "safety",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "stereotyping",
    label: "Stereotyping",
    group: "safety",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "multimodal_overall_quality",
    label: "Screenshot: overall quality",
    group: "multimodal",
    phase: 1,
    needsRubric: false,
    isMultimodal: true,
  },
  {
    key: "multimodal_correctness",
    label: "Screenshot: correctness",
    group: "multimodal",
    phase: 1,
    needsRubric: false,
    isMultimodal: true,
  },
  {
    key: "multimodal_faithfulness",
    label: "Screenshot: faithfulness",
    group: "multimodal",
    phase: 1,
    needsRubric: false,
    isMultimodal: true,
  },
  {
    key: "multimodal_instruction_following",
    label: "Screenshot: instruction following",
    group: "multimodal",
    phase: 1,
    needsRubric: false,
    isMultimodal: true,
  },
  {
    key: "goal_success_rate",
    label: "Goal success rate",
    group: "agentic",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    key: "tool_selection_accuracy",
    label: "Tool selection accuracy",
    group: "agentic",
    phase: 1,
    needsRubric: false,
    isMultimodal: false,
  },
  {
    // Judging argument correctness needs structured tool-call capture (Phase 3b); greyed.
    key: "tool_parameter_accuracy",
    label: "Tool parameter accuracy",
    group: "agentic",
    phase: 3,
    needsRubric: false,
    isMultimodal: false,
  },
] as const;

// Curated judge/grader model presets for the eval-config UI (AI-340). These are convenience
// presets only — the fields stay free-text via a "Custom…" escape, so any litellm model id works.
// Values are provider-prefixed litellm ids; the ai-service default grader is
// `anthropic/claude-sonnet-4-6` (agent_eval/run.py). Empty string ⇒ inherit that default.
export const AGENT_EVAL_JUDGE_MODELS = [
  {
    value: "anthropic/claude-sonnet-4-6",
    label: "Claude Sonnet 4.6 — default",
  },
  {
    value: "anthropic/claude-opus-4-8",
    label: "Claude Opus 4.8 — most thorough",
  },
  {
    value: "anthropic/claude-haiku-4-5-20251001",
    label: "Claude Haiku 4.5 — fastest & cheapest",
  },
] as const;

// Mirrors the Inspect harness RunOutcome + the safety veto (agent_eval/schema.py).
const OutcomeSchema = z.enum([
  "agent_pass",
  "agent_fail",
  "environment_unavailable",
  "environment_changed",
  "safety_violation",
]);

// ============================================================================
// Response shapes
// ============================================================================

const AgentEvalSuiteResponseSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  family: z.string().nullable(), // "swag_labs" / "orangehrm" / ...
  createdBy: UUIDSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  caseCount: z.number().int().nonnegative().optional(),
  // The run config the suite carries (Phase 2). diagnosis/simulator configs are
  // free-form blobs until Phases 3/4 give them a concrete shape.
  evaluators: z.array(EvaluatorConfigSchema).default([]),
  // AI-340: the model judging this suite's primary success-criteria rubric on a suite run; null ⇒
  // the ai-service default grader. A run-level graderModel override still wins.
  graderModel: z.string().nullable().default(null),
  diagnosisConfig: JsonObjectSchema.nullable().default(null),
  simulatorConfig: JsonObjectSchema.nullable().default(null),
});

// ---- Authored test-case primitives (Phase 2) ----
// The expected structured answer for a case (mirrors the ai-service ExpectedOutput).
const ExpectedOutputSchema = z.object({
  completed: z.boolean().nullable().default(null),
  items: z.array(z.string()).nullable().default(null),
  confirmationTextContains: z.string().nullable().default(null),
  finalState: z.string().nullable().default(null),
  mustReportFields: z.array(z.string()).default([]),
});

// Where the run starts + how to tell the site drifted (mirrors EnvironmentConfig).
const EnvironmentConfigSchema = z.object({
  startUrl: z.string(),
  credentials: z.record(z.string(), z.string()).default({}),
  healthCheckUrl: z.string().nullable().default(null),
  driftMarkers: z.array(z.string()).default([]),
});

// Goal-driven simulated user for HITL flows (Phase 4 UserSimulator → Strands ActorProfile).
const ActorProfileSchema = z.object({
  traits: z.string(),
  context: z.string().nullable().default(null),
  actorGoal: z.string(),
});

// An authored case. `scenarioKey` is the stable id used as case-result scenarioId.
const TestCaseResponseSchema = z.object({
  id: UUIDSchema,
  suiteId: UUIDSchema,
  scenarioKey: z.string(),
  name: z.string(),
  site: z.string().nullable(),
  task: z.string(),
  successCriteria: z.array(z.string()),
  expectedOutput: ExpectedOutputSchema,
  safetyConstraints: z.array(z.string()),
  readOnly: z.boolean(),
  environment: EnvironmentConfigSchema,
  actorProfile: ActorProfileSchema.nullable(),
  tags: z.array(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const AgentEvalRunSummarySchema = z.object({
  totalCases: z.number().int().nonnegative(),
  agentPass: z.number().int().nonnegative(),
  agentFail: z.number().int().nonnegative(),
  environmentFailures: z.number().int().nonnegative(),
  safetyViolations: z.number().int().nonnegative(),
  rubricItemsMet: z.number().int().nonnegative(),
  rubricItemsTotal: z.number().int().nonnegative(),
  totalCostUsd: z.number().nullable(),
  regressions: z.number().int().nonnegative(),
  improvements: z.number().int().nonnegative(),
});

// Live view (AI-235) — ephemeral state for the currently-running case, streamed
// from the ai-service mid-run so the UI can show the operator working in real
// time (the live browser URL + a lightweight, already-redacted turn feed). It is
// null on any non-running run; the authoritative case result (full trajectory +
// screenshots) lands in the case-results list on completion. Single object —
// samples run sequentially, so at most one case is live at a time.
const LiveTurnSchema = z.object({
  index: z.number().int().nonnegative(),
  role: z.string(),
  text: z.string(),
  action: z.string().nullable(),
});

const LiveStateSchema = z.object({
  scenarioId: z.string(),
  status: z.literal("running"),
  // The case's phase within a running run: "operating" while the agent drives
  // the browser (live view is live), "scoring" once it finishes and the
  // evaluators run (live browser is gone — the UI shows an evaluating state).
  phase: z.enum(["operating", "scoring"]).default("operating"),
  liveViewUrl: z.string().nullable(),
  turns: z.array(LiveTurnSchema),
  // Monotonic per-case high-water mark (the operator step index). Lets the
  // backend reject stale/out-of-order live posts under the throttle.
  seq: z.number().int().nonnegative(),
});

const AgentEvalRunResponseSchema = z.object({
  id: UUIDSchema,
  suiteId: UUIDSchema,
  status: RunStatusSchema,
  summary: AgentEvalRunSummarySchema.nullable(),
  previousRunId: UUIDSchema.nullable(),
  provider: z.string().nullable(), // anthropic / openai
  model: z.string().nullable(),
  compute: z.string().nullable(), // daytona / local
  startedByName: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  // AI-generated performance summary (alignment prose), null when not generated.
  narrative: z.string().nullable(),
  liveState: LiveStateSchema.nullable(),
  createdAt: z.string(),
});

// One rubric criterion's verdict — `cites` are turn indices the review UI links
// to the trajectory message + its screenshot (spec §10). `source` distinguishes the
// success-criteria judge ("rubric") from a Strands evaluator ("strands"); `score` is the
// Strands evaluator's 0–1 score (null for the boolean success-criteria rubric).
const RubricItemResultSchema = z.object({
  id: z.string(),
  criterion: z.string(),
  met: z.boolean(),
  reasoning: z.string(),
  cites: z.array(z.number().int().nonnegative()),
  score: z.number().nullable().default(null),
  source: z.enum(["rubric", "strands"]).default("rubric"),
});

// One design-by-contract per-element verdict for a run (mirrors the ai-service ContractCheck).
// `id` is the stable cross-run aggregation key; `status` is tri-state (not_applicable = the state
// this element governs was never reached); `source` is the oracle that produced it.
const ContractCheckSchema = z.object({
  id: z.string(),
  kind: z.enum(["pre", "post", "do", "dont"]),
  status: z.enum(["held", "violated", "not_applicable"]),
  source: z.enum(["eval_state", "verify", "judge"]),
  reasoning: z.string().default(""),
  cites: z.array(z.number().int().nonnegative()).default([]),
});

// A trajectory turn, with the tool-call action + screenshot(s) taken on it (the
// screenshot<->message link). Action/screenshots ride the same agent message.
const TrajectoryTurnSchema = z.object({
  index: z.number().int().nonnegative(),
  role: z.string(),
  text: z.string(),
  action: z.string().nullable(),
  screenshots: z.array(z.string()).default([]),
});

const AgentEvalCaseResultResponseSchema = z.object({
  id: UUIDSchema,
  runId: UUIDSchema,
  scenarioId: z.string(),
  task: z.string().nullable(),
  outcome: OutcomeSchema,
  rubricMet: z.number().int().nonnegative(),
  rubricTotal: z.number().int().nonnegative(),
  rubricItems: z.array(RubricItemResultSchema),
  // Per-element design-by-contract verdicts (pre/post/DO/DON'T), each stably id'd for the
  // cross-run alignment scorecard. Empty for a run with no contract (e.g. public-web).
  contractChecks: z.array(ContractCheckSchema).default([]),
  trajectory: z.array(TrajectoryTurnSchema),
  costUsd: z.number().nullable(),
  latencyMs: z.number().int().nullable(),
  steps: z.number().int().nullable(),
  langfuseSession: z.string().nullable(),
  liveViewUrl: z.string().nullable(),
  // Session-replay recording (kernel compute only) — a Document id the review
  // page resolves to a signed URL to play the run's video. Null when unrecorded.
  recordingDocumentId: UUIDSchema.nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
});

// ============================================================================
// Ingest — driven by the Inspect lifecycle hooks (definitions/hooks/gently_ingest.py)
// so progress streams as the eval runs: a run is created on task-start, each
// scenario is recorded on sample-end, the run is finalized on task-end.
//
// NOTE: ingest is NOT oRPC — it's API-key-authenticated Express (the eval run
// has no user session; oRPC is session-authed). These schemas are exported so
// the Express handlers validate with them and the Python hook shares the types.
// The start handler upserts the suite by name (the YAML dataset owns scenarios).
// ============================================================================

// POST /v1/agent-evals/runs — open a run (status=running) on task-start.
// `suiteId`, when present, attaches the run to that authored suite (Phase 2 run-wiring);
// otherwise the suite is upserted by `suiteName` (the bundled YAML dataset path).
export const StartRunInputSchema = z.object({
  // The eval runs under this org. A machine caller (the ai-service) has no session, so
  // — like the sandboxes.* service surface — it declares the org on every ingest call;
  // the backend verifies the run belongs to that org (the write authorization boundary).
  organizationId: UUIDSchema,
  // The user who triggered the run (attribution / triggeredBy); null for non-user callers.
  userId: UUIDSchema.nullable().optional(),
  suiteName: z.string().min(1).max(255),
  suiteId: UUIDSchema.nullable().optional(),
  family: z.string().nullable().optional(),
  provider: z.string().nullable().optional(),
  model: z.string().nullable().optional(),
  compute: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
});

// POST /v1/agent-evals/runs/:runId/results — record one scenario on sample-end.
// `family`/`startUrl`/`successCriteria`/`safetyConstraints`/`readOnly` are scenario
// context the hook can send for forward-compat; only the modeled columns persist today.
export const IngestCaseResultSchema = z.object({
  // Write authorization boundary: the backend rejects the post unless this run belongs to org.
  organizationId: UUIDSchema,
  scenarioId: z.string(),
  task: z.string(),
  family: z.string().nullable().optional(),
  startUrl: z.string().nullable().optional(),
  successCriteria: z.array(z.string()).default([]),
  safetyConstraints: z.array(z.string()).default([]),
  readOnly: z.boolean().default(false),
  outcome: OutcomeSchema,
  rubricMet: z.number().int().nonnegative(),
  rubricTotal: z.number().int().nonnegative(),
  rubricItems: z.array(RubricItemResultSchema).default([]),
  contractChecks: z.array(ContractCheckSchema).default([]),
  trajectory: z.array(TrajectoryTurnSchema).default([]),
  costUsd: z.number().nullable().optional(),
  latencyMs: z.number().int().nullable().optional(),
  steps: z.number().int().nullable().optional(),
  langfuseSession: z.string().nullable().optional(),
  liveViewUrl: z.string().nullable().optional(),
  // Session-replay recording document id (kernel compute only), uploaded by the
  // driver before the result post. Absent/null when the run wasn't recorded.
  recordingDocumentId: UUIDSchema.nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  // Procedure-replay drift counts (AI-246 / Fable #5): how many steps ran deterministically vs.
  // needed a self-heal vs. fell back to the agent. Null for a natural-language scenario (not a
  // replay). Persisted so drift is trackable over time instead of being stripped here.
  deterministicSteps: z.number().int().nonnegative().nullable().optional(),
  healedSteps: z.number().int().nonnegative().nullable().optional(),
  agenticSteps: z.number().int().nonnegative().nullable().optional(),
});

// POST /v1/agent-evals/runs/:runId/live — stream live state for the in-progress
// case (AI-235). Best-effort, fire-and-forget from the ai-service. `turns` is the
// full current (capped) list — replace-all, so a dropped throttled post is
// harmless and there is no append race. `liveViewUrl` is sent on case start.
export const IngestLiveStateSchema = z.object({
  // Write authorization boundary: the backend rejects the post unless this run belongs to org.
  organizationId: UUIDSchema,
  scenarioId: z.string(),
  liveViewUrl: z.string().nullable().optional(),
  phase: z.enum(["operating", "scoring"]).optional(),
  turns: z.array(LiveTurnSchema).default([]),
  seq: z.number().int().nonnegative(),
});

// PATCH /v1/agent-evals/runs/:runId — finalize on task-end (terminal status only).
export const FinalizeRunInputSchema = z.object({
  // Write authorization boundary: the backend rejects the post unless this run belongs to org.
  organizationId: UUIDSchema,
  status: z.enum(["completed", "failed"]),
  completedAt: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  evalLogUri: z.string().nullable().optional(),
  // AI-generated per-run performance summary (alignment prose), when the harness produced one.
  narrative: z.string().nullable().optional(),
});

// ============================================================================
// Input schemas (read side)
// ============================================================================

const ListSuitesInputSchema = z.object({}).optional();
const ListRunsInputSchema = z.object({ suiteId: UUIDSchema });
const GetRunInputSchema = z.object({ runId: UUIDSchema });

// Cross-run alignment roll-up (AI-340 Phase 2) — one row per (contract_id, source) across a suite's
// runs, optionally within the last `windowDays`.
const ContractAlignmentInputSchema = z.object({
  suiteId: UUIDSchema,
  windowDays: z.number().int().positive().optional(),
});
const ContractAlignmentRowSchema = z.object({
  id: z.string(),
  kind: z.enum(["pre", "post", "do", "dont"]),
  source: z.enum(["eval_state", "verify", "judge"]),
  held: z.number().int().nonnegative(),
  violated: z.number().int().nonnegative(),
  notApplicable: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});
const ContractAlignmentResponseSchema = z.object({
  rows: z.array(ContractAlignmentRowSchema),
  runCount: z.number().int().nonnegative(),
  caseCount: z.number().int().nonnegative(),
});

// AI-generated narrative for the cross-run alignment scorecard (Phase 2). Best-effort — an empty
// narrative when there's nothing to summarize or generation is unavailable/failed.
const SummarizeAlignmentInputSchema = z.object({
  suiteId: UUIDSchema,
  windowDays: z.number().int().positive().optional(),
});
const SummarizeAlignmentResponseSchema = z.object({ narrative: z.string() });

// ---- Authoring CRUD inputs (Phase 2) ----
const CreateSuiteInputSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  family: z.string().nullable().optional(),
  evaluators: z.array(EvaluatorConfigSchema).optional(),
  graderModel: z.string().nullable().optional(),
});

const UpdateSuiteInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).max(255).optional(),
  description: z.string().nullable().optional(),
  family: z.string().nullable().optional(),
  evaluators: z.array(EvaluatorConfigSchema).optional(),
  graderModel: z.string().nullable().optional(),
  diagnosisConfig: JsonObjectSchema.nullable().optional(),
  simulatorConfig: JsonObjectSchema.nullable().optional(),
});

const ListTestCasesInputSchema = z.object({ suiteId: UUIDSchema });

const CreateTestCaseInputSchema = z.object({
  suiteId: UUIDSchema,
  scenarioKey: z.string().min(1).max(255),
  name: z.string().min(1).max(255),
  site: z.string().nullable().optional(),
  task: z.string().min(1),
  successCriteria: z.array(z.string()).default([]),
  expectedOutput: ExpectedOutputSchema.default({
    completed: null,
    items: null,
    confirmationTextContains: null,
    finalState: null,
    mustReportFields: [],
  }),
  safetyConstraints: z.array(z.string()).default([]),
  readOnly: z.boolean().default(false),
  environment: EnvironmentConfigSchema,
  actorProfile: ActorProfileSchema.nullable().optional(),
  tags: z.array(z.string()).default([]),
});

// Update is the editable subset by id (scenarioKey is the stable join key — not editable).
const UpdateTestCaseInputSchema = z.object({
  id: UUIDSchema,
  name: z.string().min(1).max(255).optional(),
  site: z.string().nullable().optional(),
  task: z.string().min(1).optional(),
  successCriteria: z.array(z.string()).optional(),
  expectedOutput: ExpectedOutputSchema.optional(),
  safetyConstraints: z.array(z.string()).optional(),
  readOnly: z.boolean().optional(),
  environment: EnvironmentConfigSchema.optional(),
  actorProfile: ActorProfileSchema.nullable().optional(),
  tags: z.array(z.string()).optional(),
});

const DeleteTestCaseInputSchema = z.object({ id: UUIDSchema });

// ---- Case generation (Phase 5) — draft candidate cases from a description ----
const GenerateCasesInputSchema = z.object({
  description: z.string().min(1).max(2000),
  numCases: z.number().int().min(1).max(12).optional(),
  numTopics: z.number().int().min(1).max(6).optional(),
});

// A generated draft — name + task + suggested criteria. The author completes the
// environment (start URL / credentials) in the case form before saving via CRUD.
const GeneratedCaseSchema = z.object({
  name: z.string(),
  task: z.string(),
  successCriteria: z.array(z.string()),
});

// ---- Run an eval straight from a Mock UI Studio build (AI-340) ----
// The build's cases become a reusable eval suite (one case per `?case=`, pointed at the live
// preview URL), then a rubric-graded operator run is triggered. v1 is rubric-only: the LLM judge
// grades each case's successCriteria — works on any built mock. Deterministic `__evalState` scoring
// is a follow-up (needs per-case scorer selection in the harness).
const RunMockUiEvalInputSchema = z.object({
  /** The live signed preview URL of the built mock; each case runs against `${previewUrl}?case=<id>`. */
  previewUrl: z.string().url(),
  /** The mock's site/folder — becomes the suite's logical site key. */
  site: z.string().max(255).default(""),
  /** Suite name shown in Agent Evals (defaults to a name derived from the site). */
  suiteName: z.string().max(255).default(""),
  compute: z.enum(["kernel", "local"]).default("kernel"),
  /** AI-340 B4: the LLM judge for this rubric run, chosen at the studio "Run eval". Null ⇒ the
   * ai-service default grader. (Deterministic runs are LLM-free, so this doesn't apply there.) */
  graderModel: z.string().nullable().optional(),
  /** One entry per built `?case=`: the operator task + its plain-English pass condition. */
  cases: z
    .array(
      z.object({
        id: z.string().min(1),
        task: z.string().default(""),
        successCriteria: z.string().default(""),
      }),
    )
    .min(1)
    .max(20),
});

// ---- Run the DETERMINISTIC procedure eval from a Mock UI Studio build (AI-340) ----
// Each `?case=` that has a taught SOP (startUrl = `${hostedUrl}?case=<id>`) becomes a
// `procedure_replay` case: the ai-service replays the SOP and grades its captured
// `window.__evalState` against the SOP-version golden by subset-equality — no LLM. This is the
// deterministic counterpart to the rubric-only `runMockUiEval`; the studio calls it when the mock
// has taught procedures, and falls back to `runMockUiEval` otherwise.
const RunMockUiProcedureEvalInputSchema = z.object({
  /** The mock's durable hosted URL; its taught SOPs are the ones whose startUrl starts with it. */
  hostedUrl: z.string().url(),
  /** The mock's site/folder — becomes the suite's logical site key. */
  site: z.string().max(255).default(""),
  /** Suite name shown in Agent Evals (defaults to a name derived from the site). */
  suiteName: z.string().max(255).default(""),
  compute: z.enum(["kernel", "local"]).default("kernel"),
});

// ---- List the SOPs taught against a mock, keyed by `?case=` (AI-340) ----
// Powers the studio's per-case teach/golden status and the state-graph SOP overlay.
const ListMockUiTaughtSopsInputSchema = z.object({
  hostedUrl: z.string().url(),
});

export const MockUiTaughtSopSchema = z.object({
  caseId: z.string(),
  sopId: UUIDSchema,
  name: z.string(),
  version: z.number().int(),
  /** The captured golden `window.__evalState`, or null until a clean replay bootstraps it. */
  golden: z.record(z.string(), z.unknown()).nullable(),
  /** Concrete parameter bindings stored on this mock case. */
  parameters: z.record(z.string(), z.string()).default({}),
});

// One correlated mock-version row (AI-340, Step 7). Lines up the OFFLINE eval pass-rate against the
// ONLINE production success + drift rate for the same mock version, so a green offline eval sitting
// next to a low online success rate / high drift rate surfaces the real site having drifted from
// what the mock encodes. Keyed by mock version (the dimension both sides reliably share; the offline
// run's sopVersion is null as a suite spans many). Rates are 0–1 fractions, null when no runs.
export const EvalCorrelationRowSchema = z.object({
  mockSiteVersionId: UUIDSchema.nullable(),
  offlineRunCount: z.number().int(),
  offlineCaseCount: z.number().int(),
  offlinePassRate: z.number().nullable(),
  onlineRunCount: z.number().int(),
  onlineSuccessRate: z.number().nullable(),
  onlineDriftRate: z.number().nullable(),
});

// ============================================================================
// Contract
// ============================================================================

export const agentEvalsContracts = {
  listSuites: oc
    .input(ListSuitesInputSchema)
    .output(z.array(AgentEvalSuiteResponseSchema))
    .route({ method: "GET" }),

  listRuns: oc
    .input(ListRunsInputSchema)
    .output(z.array(AgentEvalRunResponseSchema))
    .route({ method: "GET" }),

  getRun: oc
    .input(GetRunInputSchema)
    .output(
      z.object({
        run: AgentEvalRunResponseSchema,
        results: z.array(AgentEvalCaseResultResponseSchema),
      }),
    )
    .route({ method: "GET" }),

  // Cross-run alignment scorecard (AI-340 Phase 2): per-(contract_id, source) held/violated/n-a
  // across the suite's runs. Org-scoped; DB-only (no LLM).
  contractAlignment: oc
    .input(ContractAlignmentInputSchema)
    .output(ContractAlignmentResponseSchema)
    .route({ method: "GET" }),

  // AI narrative for the alignment scorecard (Phase 2). POST — it triggers an LLM call. Best-effort.
  summarizeAlignment: oc
    .input(SummarizeAlignmentInputSchema)
    .output(SummarizeAlignmentResponseSchema)
    .route({ method: "POST" }),

  // AI-340, Step 7 — the offline↔online correlation read. Aggregates the AgentRun trunk by mock
  // version and returns the offline eval pass-rate beside the online production success + drift
  // rate, so drift the offline evals missed is visible. Org-scoped (session-authed).
  getEvalCorrelation: oc
    // `site` scopes the correlation to one mock (AI-385) — it now lives on the mock, not the evals
    // tab. Omitted ⇒ org-wide (legacy behavior).
    .input(z.object({ site: z.string().optional() }).optional())
    .output(z.array(EvalCorrelationRowSchema))
    .route({ method: "GET" }),

  // ---- Authoring CRUD (Phase 2) — session-authed, org-scoped ----
  createSuite: oc
    .input(CreateSuiteInputSchema)
    .output(AgentEvalSuiteResponseSchema)
    .route({ method: "POST" }),

  updateSuite: oc
    .input(UpdateSuiteInputSchema)
    .output(AgentEvalSuiteResponseSchema)
    .route({ method: "POST" }),

  listTestCases: oc
    .input(ListTestCasesInputSchema)
    .output(z.array(TestCaseResponseSchema))
    .route({ method: "GET" }),

  createTestCase: oc
    .input(CreateTestCaseInputSchema)
    .output(TestCaseResponseSchema)
    .route({ method: "POST" }),

  updateTestCase: oc
    .input(UpdateTestCaseInputSchema)
    .output(TestCaseResponseSchema)
    .route({ method: "POST" }),

  deleteTestCase: oc
    .input(DeleteTestCaseInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),

  // Trigger a new agent-eval run. Session-authed; the handler forwards to the
  // ai-service run endpoint, which executes the eval and streams results back via
  // the ingest API (the UI's polling then surfaces the new run).
  run: oc
    .input(
      z.object({
        provider: z.enum(["anthropic", "openai"]).optional(),
        compute: z.enum(["kernel", "local"]).optional(),
        // Which agent driver runs the eval; defaults to "operator" (the AI-244
        // browser skill) server-side. A second driver would be added here.
        driver: z.string().optional(),
        graderModel: z.string().optional(),
        // Strands evaluator catalog selection + scenario subset (both optional —
        // omitting them runs today's two scorers over the full dataset).
        evaluators: z.array(EvaluatorConfigSchema).optional(),
        scenarioIds: z.array(z.string()).optional(),
        // Run an authored suite: the handler resolves its cases + default evaluators
        // and sends them inline to the ai-service, which attaches the run to this suite.
        // Omit to run the bundled YAML dataset (today's behavior).
        suiteId: UUIDSchema.optional(),
        // Target procedures (SOPs) instead of scenarios (AI-246 follow-on). The handler
        // resolves each procedure and runs it either via deterministic `operator.run_sop`
        // replay (`replay`, default) or by handing it to the agentic operator as a task
        // (`agentic`). Each procedure carries `parameters` (literal values for the SOP's
        // declared inputs) bound into the resolved plan, so a parameterized SOP replays
        // exactly as it would in production. `procedureAgentId` is the agent-aware path:
        // the handler pulls that agent's setup/teardown `sop` phases (their sopId + bound
        // parameters) and evals exactly those — leave `procedures` empty when using it.
        // `procedureStartUrl` is the app URL to run against — required for `agentic` (SOPs
        // store no start URL); optional for `replay` (the recorded steps navigate).
        procedures: z
          .array(
            z.object({
              sopId: UUIDSchema,
              parameters: z.record(z.string(), z.string()).optional(),
            }),
          )
          .optional(),
        procedureAgentId: UUIDSchema.optional(),
        procedureMode: z.enum(["replay", "agentic"]).optional(),
        procedureStartUrl: z.string().url().optional(),
      }),
    )
    .output(z.object({ status: z.literal("started") }))
    .route({ method: "POST" }),

  // Seed a reusable eval suite from a Mock UI Studio build and trigger a rubric-graded run against
  // its live preview URL. Returns the new suite id (the run attaches to it via the ingest hook).
  runMockUiEval: oc
    .input(RunMockUiEvalInputSchema)
    .output(z.object({ suiteId: UUIDSchema }))
    .route({ method: "POST" }),

  // Run the DETERMINISTIC procedure eval over a mock's taught SOPs (one procedure_replay case per
  // taught `?case=`, graded against the SOP-version golden __evalState). Returns the new suite id.
  runMockUiProcedureEval: oc
    .input(RunMockUiProcedureEvalInputSchema)
    .output(z.object({ suiteId: UUIDSchema }))
    .route({ method: "POST" }),

  // The SOPs taught against a mock, keyed by `?case=` — the deterministic-eval gate + graph overlay.
  listMockUiTaughtSops: oc
    .input(ListMockUiTaughtSopsInputSchema)
    .output(z.object({ sops: z.array(MockUiTaughtSopSchema) }))
    .route({ method: "GET" }),

  // Stop a running run. Marks it `cancelled` (a no-op if already terminal) and
  // drops its live panel; the ai-service task finishes in the background but its
  // ingest posts are rejected. Returns the run's status after the attempt.
  cancelRun: oc
    .input(z.object({ runId: UUIDSchema }))
    .output(z.object({ status: RunStatusSchema }))
    .route({ method: "POST" }),

  // Draft candidate cases from a description (Phase 5). Forwards to the ai-service
  // generator; returns drafts the UI previews and saves via createTestCase.
  generateCases: oc
    .input(GenerateCasesInputSchema)
    .output(z.object({ cases: z.array(GeneratedCaseSchema) }))
    .route({ method: "POST" }),
} as const;

export type AgentEvalSuiteResponse = z.infer<
  typeof AgentEvalSuiteResponseSchema
>;
export type AgentEvalRunResponse = z.infer<typeof AgentEvalRunResponseSchema>;
export type AgentEvalCaseResultResponse = z.infer<
  typeof AgentEvalCaseResultResponseSchema
>;
export type EvalCorrelationRow = z.infer<typeof EvalCorrelationRowSchema>;
export type AgentEvalRunSummaryResponse = z.infer<
  typeof AgentEvalRunSummarySchema
>;
export type RubricItemResult = z.infer<typeof RubricItemResultSchema>;
export type ContractCheck = z.infer<typeof ContractCheckSchema>;
export type ContractAlignmentRow = z.infer<typeof ContractAlignmentRowSchema>;
export type ContractAlignmentResponse = z.infer<
  typeof ContractAlignmentResponseSchema
>;
export type TrajectoryTurn = z.infer<typeof TrajectoryTurnSchema>;
export type AgentEvalLiveTurn = z.infer<typeof LiveTurnSchema>;
export type AgentEvalRunLiveState = z.infer<typeof LiveStateSchema>;
export type EvaluatorConfig = z.infer<typeof EvaluatorConfigSchema>;
export type AgentEvalEvaluatorCatalogEntry =
  (typeof AGENT_EVAL_EVALUATOR_CATALOG)[number];
export type AgentEvalTestCaseResponse = z.infer<typeof TestCaseResponseSchema>;
export type AgentEvalExpectedOutput = z.infer<typeof ExpectedOutputSchema>;
export type AgentEvalEnvironmentConfig = z.infer<
  typeof EnvironmentConfigSchema
>;
export type AgentEvalActorProfile = z.infer<typeof ActorProfileSchema>;
export type CreateAgentEvalSuiteInput = z.infer<typeof CreateSuiteInputSchema>;
export type UpdateAgentEvalSuiteInput = z.infer<typeof UpdateSuiteInputSchema>;
export type GenerateAgentEvalCasesInput = z.infer<
  typeof GenerateCasesInputSchema
>;
export type GeneratedAgentEvalCase = z.infer<typeof GeneratedCaseSchema>;
export type CreateAgentEvalTestCaseInput = z.infer<
  typeof CreateTestCaseInputSchema
>;
export type UpdateAgentEvalTestCaseInput = z.infer<
  typeof UpdateTestCaseInputSchema
>;
export type StartRunInput = z.infer<typeof StartRunInputSchema>;
export type IngestCaseResultInput = z.infer<typeof IngestCaseResultSchema>;
export type IngestLiveStateInput = z.infer<typeof IngestLiveStateSchema>;
export type FinalizeRunInput = z.infer<typeof FinalizeRunInputSchema>;
